import { createTheme } from '@mui/material/styles';
import { viVN } from '@mui/material/locale';
import { common } from '@mui/material/colors';

// A custom theme for this app
const theme = createTheme(
  {
    palette: {
      primary: {
        light: '#FFF2EA',
        main: '#FE5F00',
        dark: '#EA5800',
      },
      divider: '#f3f4f8',
      background: {
        default: '#f2f4f8',
        paper: '#fff',
      },
      common: {
        black: '#121619',
        white: '#fff',
      },
      text: {
        primary: '#121619',
        secondary: '#697077',
        disabled: '#C4C4C4',
      },
      grey: {
        100: '#121619',
        400: '#A2A9B0',
        300: '#DDE1E6',
        500: '#697077',
        600: '#878D96',
        700: '#343A3F',
        900: '#121619',
      },
      // error: {},
      // success: {},
      // warning: {},
      // info: {},
      secondary: {
        light: '#F1F8FF',
        main: '#F1F8FF',
        dark: '#096DD9',
        contrastText: '#fff',
      },
    },
    typography: {
      allVariants: {
        fontFamily: `SVN Gilroy, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"`,
      },
      h1: {
        fontSize: 'clamp(2.625rem, 1.2857rem + 3.5714vw, 4rem)',
        fontWeight: 700,
        lineHeight: 1.1142857142857143,
      },
      h2: {
        fontSize: 'clamp(1.5rem, 0.9643rem + 1.4286vw, 2.25rem)',
        fontWeight: 700,
        lineHeight: '2.5rem',
      },
      h3: {
        fontSize: '2.25rem',
        lineHeight: '2.5rem',
        fontWeight: 400,
      },
      h4: {
        fontSize: '1.875rem',
        lineHeight: '2.25rem',
        fontWeight: 400,
      },
      h5: {
        fontSize: '1.5rem',
        lineHeight: '2rem',
        fontWeight: 500,
      },
      h6: {
        fontSize: '1.25rem',
        lineHeight: '1.75rem',
        fontWeight: 700,
      },
      subtitle1: {
        fontSize: '1.25rem',
        lineHeight: '1.75rem',
        fontWeight: 700,
      },
      subtitle2: {
        fontWeight: 700,
        fontSize: '0.875rem',
        lineHeight: '1.25rem',
      },
      body1: {
        fontSize: '1rem',
        lineHeight: '1.5rem',
        fontWeight: 500,
      },
      body2: {
        fontSize: '0.875rem',
        lineHeight: '1.25rem',
        fontWeight: 500,
      },
      caption: {
        fontSize: '0.75rem',
        lineHeight: '1rem',
        fontWeight: 500,
      },
      overline: {
        fontWeight: 500,
        fontSize: '0.75rem',
        lineHeight: 2.66,
        textTransform: 'uppercase',
      },
    },
    components: {
      MuiIconButton: {
        styleOverrides: {
          sizeSmall: {
            padding: 4,
          },
          colorPrimary: {
            backgroundColor: '#FFF2EA',
            color: '#EA5800',
            ':hover': {
              backgroundColor: '#FFF2EA',
            },
            ':disabled': {
              backgroundColor: 'rgba(0, 0, 0, 0.12)',
            },
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            display: 'inline-block',
            textTransform: 'initial',
            fontWeight: 700,
            fontSize: '0.875rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
          fullWidth: {
            display: 'flex',
          },
          sizeLarge: {
            padding: '12px 16px',
          },

          textPrimary: {
            backgroundColor: '#FFF2EA',
            color: '#EA5800',
            ':hover': {
              backgroundColor: '#FFF2EA',
            },
            ':disabled': {
              backgroundColor: 'rgba(0, 0, 0, 0.12)',
            },
          },

          // textPrimary: {
          //   backgroundColor: '#FFF2EA',
          //   color: '#EA5800',
          //   ':disabled': {
          //     backgroundColor: '#E6E9EC',
          //     svg: {
          //       opacity: 0.2,
          //     },
          //   },
          // },
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: {
            backgroundColor: common.white,
            minHeight: 52,
            paddingLeft: 8,
            paddingRight: 8,
            '@media (min-width: 600px)': {
              minHeight: 52,
              paddingLeft: 8,
              paddingRight: 8,
            },
          },
        },
      },
      MuiList: {
        styleOverrides: {
          root: {
            backgroundColor: '#fff',
            paddingTop: 0,
            paddingBottom: 0,
            '.MuiListSubheader-root': {
              borderBottom: '1px solid #f3f4f8',
              zIndex: 20,
              '+ .MuiListItem-root': {
                borderTop: 'none !important',
              },
            },
          },
        },
      },
      MuiListItem: {
        styleOverrides: {
          root: {
            // backgroundColor: '#fff',
            paddingTop: 12,
            paddingBottom: 12,
            '&a': {
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.025)' },
            },
          },
          button: {
            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.025)' },
          },
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            marginTop: 4,
          },
        },
      },
      MuiListItemText: {
        styleOverrides: {
          root: {
            marginTop: 0,
            marginBottom: 0,
          },
        },
      },
      MuiListSubheader: {
        styleOverrides: {
          root: {
            zIndex: 5,
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          message: {
            // padding: 0,
          },
          root: {
            fontSize: '0.875rem',
            lineHeight: '1.25rem',
            fontWeight: 500,
            paddingTop: 8,
            paddingBottom: 8,
          },
          standardInfo: {
            backgroundColor: '#F1F8FF',
            color: '#121619',
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            padding: '14px 16px',
            fontSize: '1rem',
            lineHeight: '1.5rem',
            fontWeight: 500,
            textTransform: 'initial',
            color: '#697077',
          },
          textColorPrimary: {
            '&.Mui-selected': {
              color: '#121619',
              fontWeight: 700,
            },
          },
        },
      },
      MuiFab: {
        styleOverrides: {
          root: {
            boxShadow: 'none !important',
            minHeight: 48,
            height: 48,
            width: 48,
          },
          sizeSmall: {
            minHeight: 24,
            height: 24,
            width: 24,
          },
          sizeMedium: {
            minHeight: 32,
            height: 32,
            width: 32,
          },
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          root: {
            '&.MuiFormControlLabel-selection': {
              position: 'relative',
              margin: 0,

              '.MuiRadio-root': {
                display: 'none',

                '&.Mui-checked': {
                  display: 'block',
                  position: 'absolute',
                  top: 1,
                  right: 1,
                  padding: 0,

                  svg: {
                    // width: '1rem',
                    // height: '1rem',
                    // fontSize: '1rem',
                    display: 'none',
                  },

                  '+ .MuiFormControlLabel-label': {
                    backgroundColor: '#FFF2EA',
                    borderColor: '#FE5F00',
                  },
                  ':after': {
                    content: '"\u2713"',
                    width: 12,
                    height: 12,
                    backgroundColor: '#FE5F00',
                    borderRadius: '100%',
                    display: 'block',
                    color: '#fff',
                    margin: 0,
                    padding: 2,
                    fontSize: 10,
                    lineHeight: 1,
                    position: 'absolute',
                    top: 1,
                    right: 1,
                    textAlign: 'center',
                  },
                },
              },
              '.MuiFormControlLabel-label': {
                fontSize: '0.75rem',
                lineHeight: '1rem',
                fontWeight: 500,
                backgroundColor: '#F2F4F8',
                padding: '8px 16px',
                borderRadius: 4,
                border: '1px solid #F2F4F8',
              },
            },
          },
        },
      },
      MuiSnackbar: {
        defaultProps: {
          autoHideDuration: 3000,
          anchorOrigin: {
            horizontal: 'center',
            vertical: 'bottom',
          },
        },
        styleOverrides: {
          root: {
            maxWidth: 412,
          },
        },
      },
      MuiSkeleton: {
        styleOverrides: {
          root: {
            backgroundColor: 'rgba(18, 22, 25, 0.05)',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          root: {
            '.MuiDrawer-paper': {
              backgroundColor: 'transparent',
              boxShadow: 'none',

              '.MuiContainer-root': {
                minHeight: 300,
                borderTopLeftRadius: 4,
                borderTopRightRadius: 4,
                overflow: 'hidden',
                backgroundColor: '#f3f4f8',
                boxShadow:
                  '0px 8px 10px -5px rgb(0 0 0 / 20%), 0px 16px 24px 2px rgb(0 0 0 / 14%), 0px 6px 30px 5px rgb(0 0 0 / 12%)',

                '.MuiList-root': {
                  marginTop: 12,
                },
              },
            },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            minWidth: 320,
            maxWidth: 343,
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            padding: 16,
            paddingBottom: 12,
            fontSize: 20,
            lineHeight: 1.75,
            color: '#21272A',
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            padding: 16,
            paddingTop: 0,
            fontSize: 14,
            lineHeight: 1.375,
            color: '#697077',
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: 16,
            paddingTop: 0,
            '>:not(:first-of-type)': {
              marginLeft: 12,
            },
          },
        },
      },
      MuiCardActionArea: {
        styleOverrides: {
          root: {
            ':hover': {
              '.MuiCardActionArea-focusHighlight': {
                opacity: 0.025,
              },
            },
          },
        },
      },
      MuiCardHeader: {
        styleOverrides: {
          root: {
            '&.MuiCardHeader-truncate': {
              '.MuiCardHeader-content': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                span: {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
              },
            },
            '&.MuiCardHeader-center': {
              '.MuiCardHeader-action': {
                alignSelf: 'center',
              },
            },
            '.MuiCardHeader-avatar': {
              marginRight: 12,
            },
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            // backgroundColor: '#FFF2EA',
            // color: '#FE5F00',
          },
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            fontWeight: 700,
            ':before,:after': { display: 'none' },
            borderRadius: 4,
            input: {
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
          },
        },
      },
    },
  },
  viVN
);

export default theme;
